<template>
  <div>
    <div v-if="loading">
      <v-progress-linear indeterminate color="primary"></v-progress-linear>
    </div>
    <v-container fluid>
      <v-row>
        <v-col cols="md-6 sm-23">
          <v-card v-if="user != null">
            <v-card-title> User Detail </v-card-title>
            <v-divider />
            <v-card-text>
              <v-row>
                <v-col cols="md-8">
                  <v-text-field v-model="user.email" filled label="Email" :loading="emailFieldLoading"
                    :disabled="disableEmailField" @keydown.enter="updateEmail">
                  </v-text-field>
                </v-col>

                <v-col cols="md-4">
                  <v-btn @click.stop="toggleEmailField" text>
                    <v-icon>
                      {{ disableEmailField ? icons.edit : icons.tick }}
                    </v-icon>
                  </v-btn>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="md-8">
                  <v-text-field v-if="user != null" :loading="mobileNumberFieldLoading" v-model="user.mobile_number"
                    label="Mobile Number" filled :disabled="disableMobileNumberField" @keydown.enter="updateMobileNumber">
                  </v-text-field>
                </v-col>

                <v-col cols="md-4">
                  <v-btn @click.stop="toggleMobileNumberField" text>
                    <v-icon>{{
                      disableMobileNumberField ? icons.edit : icons.tick
                    }}</v-icon>
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="md-6 sm-23">
          <v-card v-if="user != null">
            <v-card-title> Subscription Details </v-card-title>
            <v-divider />
            <v-card-text>
              <div v-if="this.user !== null && this.user.subscription !== null">
                <v-simple-table>
                  <tbody>
                    <tr>
                      <td>
                        <b>Status</b>
                      </td>
                      <td>
                        <v-chip label :color="statusColor" text-color="white">
                          {{ this.user.subscription.status }}
                        </v-chip>
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <b>Order ID</b>
                        <div class="text-sm-caption">You can use this order id to look up against order id in razorpay
                          dashboard</div>
                      </td>
                      <td>
                        {{ user.subscription.order_id }}
                        <v-btn tile text @click.stop="copyContent(user.subscription.id)" title="Copy">
                          <v-icon>{{ icons.copy }}</v-icon>
                        </v-btn>
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <b>Plan ID</b>
                      </td>
                      <td>
                        {{ user.subscription.plan.id }}
                        <v-btn tile text @click.stop="copyContent(user.subscription.plan.id)" title="Copy">
                          <v-icon>{{ icons.copy }}</v-icon>
                        </v-btn>
                      </td>
                    </tr>

                    <tr>
                      <td>
                        <b>Plan Name</b>
                      </td>
                      <td>
                        {{ this.user.subscription.plan.name }}
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <b>Amount </b>
                      </td>
                      <td>₹ {{ this.user.subscription.plan.amount / 100 }}</td>
                    </tr>
                    <tr>
                      <td>
                        <b> Start date </b>
                      </td>
                      <td>
                        {{ startDate || "N/A" }}
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <b> End date </b>
                      </td>
                      <td>
                        {{ endDate || "N/A" }}
                      </td>
                    </tr>
                  </tbody>
                </v-simple-table>
                <PaymentStatusComponent v-if="user && user.subscription"  :paymentId="user.subscription.payment_id" :subscriptionId="user.subscription.id"/>
                <v-card outlined class="my-2" v-if="updatedEndDate">
                  <v-card-title>
                    Change subscription end date
                  </v-card-title>
                  <v-card-subtitle>
                    This action will not take into account user payment and subscription statuses.
                    Changing end date to future date will automatically set subscription status to `active`. For past date, subscription status will be set to `expired`.
                  </v-card-subtitle>
                  <v-card-text>
                    <v-text-field type="datetime-local" solo v-model="updatedEndDate" />
                  </v-card-text>
                  <v-card-actions>
                    <v-btn text outlined color="orange" @click="updateUserSubscription" :loading="updatingSubscription">Update end date</v-btn>
                  </v-card-actions>
                </v-card>

              </div>
              <div v-else>
                User has not subscribed to any plan
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>
<script>
import userApi from '@/api/userApi'
import { mdiCheck, mdiContentCopy, mdiPencil } from '@mdi/js'
import snackbar from '@/utils/snackbar'
import * as dateFormats from '@/constants/dateFormats'
import PaymentStatusComponent from '@/components/PaymentStatusComponent.vue'
import superadminApi from '@/api/superAdminApi'

export default {
  data () {
    return {
      updatingSubscription: false,
      user: null,
      loading: false,
      disableEmailField: true,
      disableMobileNumberField: true,
      emailFieldLoading: false,
      mobileNumberFieldLoading: false,
      updatedEndDate: null,
      icons: {
        copy: mdiContentCopy,
        edit: mdiPencil,
        tick: mdiCheck
      }
    }
  },
  mounted () {
    this.getUser()
  },
  computed: {
    statusColor () {
      switch (this.user.subscription.status) {
        case 'created':
          return 'orange'
        case 'active':
          return 'green'
        default:
          return 'primary'
      }
    },
    startDate () {
      if (this.user !== null && this.user.subscription !== null) {
        return this.$moment(this.user.subscription.start_date).format(dateFormats.FRIENDLY_DATE_3)
      }
      return null
    },
    endDate () {
      if (this.user !== null && this.user.subscription !== null) {
        return this.$moment(this.user.subscription.end_date).format(dateFormats.FRIENDLY_DATE_3)
      }
      return null
    }
  },
  methods: {
    async updateUserSubscription () {
      try {
        this.updatingSubscription = true
        const r = await superadminApi.updateUserSubscriptionEndDate({ subscription_id: this.user.subscription.id, _method: 'put', end_date: this.updatedEndDate ?? '' })
        console.log('sub resp', r)
        if (r.status === 200) {
          window.location.reload()
        }
      } finally {
        this.updatingSubscription = false
      }
    },
    toggleMobileNumberField () {
      this.disableMobileNumberField = !this.disableMobileNumberField
      if (this.disableMobileNumberField) {
        this.updateMobileNumber()
      }
    },
    toggleEmailField () {
      this.disableEmailField = !this.disableEmailField
      if (this.disableEmailField) {
        this.updateEmail()
      }
    },
    async updateEmail () {
      this.$log.info('Updating email')
      try {
        this.emailFieldLoading = true
        this.disableEmailField = true // when enter is pressed, need to update the state
        const params = {
          mobile_number: this.user.mobile_number,
          email: this.user.email,
          id: this.$route.params.id
        }
        const res = await userApi.updateUser(params)
        this.$log.info('updating email ', [res.data])
        if (res.data !== undefined) {
          this.user = res.data
          snackbar.show('Email updated successfully', 'success')
        }
      } finally {
        this.emailFieldLoading = false
      }
    },
    async updateMobileNumber () {
      this.$log.info('Updating mobile number')
      try {
        this.mobileNumberFieldLoading = true
        this.disableMobileNumberField = true // when enter is pressed, need to update the state
        const params = {
          mobile_number: this.user.mobile_number,
          email: this.user.email,
          id: this.$route.params.id
        }
        const res = await userApi.updateUser(params)
        if (res.data !== undefined) {
          this.user = res.data
          snackbar.show('Mobile number updated successfully', 'success')
        }
      } finally {
        this.mobileNumberFieldLoading = false
      }
    },
    copyContent (data) {
      this.$log.info('Content copied', [data])
      navigator.clipboard.writeText(data)
      snackbar.show('Copied to clipboard')
    },
    async getUser () {
      this.loading = true
      try {
        const r = await userApi.getUserById(this.$route.params.id)
        this.$log.info('user detail', [r.data])
        if (r.data !== undefined) {
          this.user = r.data
          if (this.user && this.user.subscription && this.user.subscription.end_date) {
            this.updatedEndDate = this.user.subscription.end_date
          }
        }
      } finally {
        this.loading = false
      }
    }
  },
  components: { PaymentStatusComponent }
}
</script>
